import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { ClientAddressPicker } from "@/components/client-address-picker";
import { ClientContactPicker } from "@/components/client-contact-picker";
import { PaymentTermsPicker } from "@/components/payment-terms-picker";
import { DeliveryMethodPicker } from "@/components/delivery-method-picker";
import { IntentButton } from "@/components/intent-button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { quotesApi, type CreateQuoteRequest } from "@/lib/quotes";
import { toast } from "@/hooks/use-toast";
import { NewClientDialog } from "@/components/new-client-dialog";
import { NewClientAddressDialog } from "@/components/new-client-address-dialog";
import { NewClientContactDialog } from "@/components/new-client-contact-dialog";
import type { ClientDto } from "@/lib/clients-accounts";
import { AddManualItemDialog, type ManualItem } from "./components/AddManualItemDialog";
import { ProductPicker } from "@/components/products/ProductPicker";
import type { ProductDetail } from "@/lib/products";

export interface NewQuoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewQuoteModal({ open, onOpenChange }: NewQuoteModalProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [billingAddressId, setBillingAddressId] = useState<number | null>(null);
  const [deliveryAddressId, setDeliveryAddressId] = useState<number | null>(null);
  const [contactId, setContactId] = useState<number | null>(null);
  const [paymentTermsId, setPaymentTermsId] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState<string>("");
  const [deliveryMethodId, setDeliveryMethodId] = useState<number | null>(null);
  const [purchaseOrder, setPurchaseOrder] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  // When the selected client changes, clear any address/contact that belonged
  // to the previous client — they would be FK-mismatched against the new one.
  useEffect(() => {
    setBillingAddressId(null);
    setDeliveryAddressId(null);
    setContactId(null);
  }, [client?.clientId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setClient(null);
      setDueDate("");
      setPaymentTermsId(null);
      setDeliveryMethodId(null);
      setPurchaseOrder("");
      setDescription("");
      setSubmitError(null);
      setItems([]);
      setSearchOpen(false);
    }
  }, [open]);

  const [items, setItems] = useState<ManualItem[]>([]);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  const saveEnabled = client !== null;

  const create = useMutation({
    mutationFn: (body: CreateQuoteRequest) => quotesApi.create(body),
    onError: (e: Error) => setSubmitError(e.message),
  });

  async function onSave() {
    setSubmitError(null);
    let q;
    try {
      q = await create.mutateAsync({
        clientId: client!.clientId,
        validUntil: dueDate === "" ? null : dueDate,
        billingAddressId,
        deliveryAddressId,
        contactId,
        paymentTermsId,
        deliveryMethodId,
        purchaseOrder: purchaseOrder.trim() === "" ? null : purchaseOrder,
        description: description.trim() === "" ? null : description.trim(),
      });
    } catch {
      return; // onError set submitError; abort
    }

    let failed = 0;
    for (const it of items) {
      try {
        await quotesApi.addItem(q.quoteId, {
          description: it.description,
          quantity: it.quantity,
          unitPriceExGst: it.unitPriceExGst,
          notes: it.notes,
        });
      } catch { failed++; }
    }
    if (failed > 0) toast.error(`${failed} item(s) couldn't be added — add them on the quote.`);

    void qc.invalidateQueries({ queryKey: ["quotes"] });
    toast.success(`Draft quote ${q.quoteRef} created.`);
    onOpenChange(false);
    void navigate(`/quotes/${q.quoteId}`);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Quote</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-[140px_1fr_160px] items-center gap-3">
              <Label className="text-right">Client</Label>
              <ClientPicker value={client} onChange={setClient} />
              <Button variant="outline" size="sm" onClick={() => setClientDialogOpen(true)}>
                + Add New Client
              </Button>
            </div>

            <div className="grid grid-cols-[140px_1fr_160px] items-center gap-3">
              <Label className="text-right">Billing Address</Label>
              <ClientAddressPicker
                clientId={client?.clientId ?? null}
                value={billingAddressId}
                onChange={setBillingAddressId}
                placeholder="Select billing address"
              />
              <Button variant="outline" size="sm" disabled={client === null}
                onClick={() => setBillingDialogOpen(true)}>
                + Add New Address
              </Button>
            </div>

            <div className="grid grid-cols-[140px_1fr_160px] items-center gap-3">
              <Label className="text-right">Delivery Address</Label>
              <ClientAddressPicker
                clientId={client?.clientId ?? null}
                value={deliveryAddressId}
                onChange={setDeliveryAddressId}
                placeholder="Select delivery address"
              />
              <Button variant="outline" size="sm" disabled={client === null}
                onClick={() => setDeliveryDialogOpen(true)}>
                + Add New Address
              </Button>
            </div>

            <div className="grid grid-cols-[140px_1fr_160px] items-center gap-3">
              <Label className="text-right">Contact Name</Label>
              <ClientContactPicker
                clientId={client?.clientId ?? null}
                value={contactId}
                onChange={setContactId}
                placeholder="Select contact"
              />
              <Button variant="outline" size="sm" disabled={client === null}
                onClick={() => setContactDialogOpen(true)}>
                + Add New Contact
              </Button>
            </div>

            <div className="grid grid-cols-[140px_1fr_160px] items-center gap-3">
              <Label className="text-right">Payment Terms</Label>
              <PaymentTermsPicker value={paymentTermsId} onChange={setPaymentTermsId} />
              <span />
            </div>

            <div className="grid grid-cols-[140px_1fr_160px] items-center gap-3">
              <Label htmlFor="dueDate" className="text-right">Due Date</Label>
              <Input id="dueDate" type="date" value={dueDate} max="9999-12-31" onChange={(e) => setDueDate(e.target.value)} className="bg-slate-100" />
              <span />
            </div>

            <div className="grid grid-cols-[140px_1fr_160px] items-center gap-3">
              <Label className="text-right">Delivery</Label>
              <DeliveryMethodPicker value={deliveryMethodId} onChange={setDeliveryMethodId} />
              <span />
            </div>

            <div className="grid grid-cols-[140px_1fr_160px] items-center gap-3">
              <Label htmlFor="purchaseOrder" className="text-right">Purchase Order</Label>
              <Input
                id="purchaseOrder"
                value={purchaseOrder}
                onChange={(e) => setPurchaseOrder(e.target.value)}
                placeholder="Type in PO, if available"
              />
              <span />
            </div>

            <div className="grid grid-cols-[140px_1fr_160px] items-start gap-3">
              <Label htmlFor="description" className="pt-2 text-right">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short description for this quote (optional)"
                rows={3}
              />
              <span />
            </div>

            <div className="grid grid-cols-[140px_1fr_160px] items-center gap-3">
              <Label className="text-right">Quote Ref</Label>
              <Input value="" placeholder="Generated on save" readOnly className="bg-muted/30" />
              <span />
            </div>
          </div>

          <div className="mt-2 flex items-center gap-2">
            <Button variant="outline" onClick={() => setSearchOpen(true)}>Search Product</Button>
            <Button variant="outline" onClick={() => setItemDialogOpen(true)}>Add Product</Button>
          </div>

          {items.length > 0 && (
            <div className="mt-4 rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="p-2">Description</th><th className="p-2 w-20">Qty</th>
                    <th className="p-2 w-28">Unit (ex)</th><th className="p-2 w-28">Line (ex)</th><th className="p-2 w-16" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="p-2">{it.description}</td>
                      <td className="p-2">{it.quantity}</td>
                      <td className="p-2">{it.unitPriceExGst.toFixed(2)}</td>
                      <td className="p-2">{(it.quantity * it.unitPriceExGst).toFixed(2)}</td>
                      <td className="p-2">
                        <button
                          type="button"
                          className="text-slate-400 hover:text-red-600 transition-colors"
                          onClick={() => setItems((prev) => prev.filter((_, j) => j !== i))}
                          aria-label="Remove item"
                        >
                          <X className="w-4 h-4" strokeWidth={2.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {searchOpen && (
            <div className="mt-4 rounded-md border p-4">
              <ProductPicker
                context={{ kind: "quote", id: 0 }}
                onCancel={() => setSearchOpen(false)}
                onAdded={() => setSearchOpen(false)}
                onPicked={(p: ProductDetail) => {
                  setItems((prev) => [...prev, {
                    description: p.productName + (p.description ? ` — ${p.description}` : ""),
                    quantity: 1,
                    unitPriceExGst: p.unitPriceExGst ?? 0,
                    notes: null,
                  }]);
                }}
              />
            </div>
          )}

          {submitError !== null ? (
            <p className="mt-4 text-sm text-destructive">{submitError}</p>
          ) : null}

          <div className="mt-6 flex justify-end gap-2 border-t pt-4">
            <IntentButton intent="ghost" onClick={() => onOpenChange(false)}>Cancel</IntentButton>
            <IntentButton
              onClick={() => { void onSave(); }}
              disabled={!saveEnabled}
              loading={create.isPending}
            >
              Save Quote
            </IntentButton>
          </div>
        </DialogContent>
      </Dialog>

      <AddManualItemDialog
        open={itemDialogOpen}
        onOpenChange={setItemDialogOpen}
        onAdd={(item) => setItems((prev) => [...prev, item])}
      />

      <NewClientDialog
        open={clientDialogOpen}
        onOpenChange={setClientDialogOpen}
        onCreated={(c: ClientDto) => setClient({
          clientId: c.clientId,
          fullName: c.fullName,
          emailAddress: c.emailAddress ?? null,
          companyId: c.companyId ?? null,
          companyName: c.companyName ?? null,
        })}
      />
      {client !== null && (
        <>
          <NewClientAddressDialog
            clientId={client.clientId}
            open={billingDialogOpen}
            onOpenChange={setBillingDialogOpen}
            onCreated={(id) => setBillingAddressId(id)}
          />
          <NewClientAddressDialog
            clientId={client.clientId}
            open={deliveryDialogOpen}
            onOpenChange={setDeliveryDialogOpen}
            onCreated={(id) => setDeliveryAddressId(id)}
          />
          <NewClientContactDialog
            clientId={client.clientId}
            open={contactDialogOpen}
            onOpenChange={setContactDialogOpen}
            onCreated={(id) => setContactId(id)}
          />
        </>
      )}
    </>
  );
}
